// PresencePanel - Collapsible bottom panel showing workspace participants
// Implements US-4.1: WebSocket Infrastructure and Presence System
// Displays list of connected users and viewers in real-time

import React, { useState } from 'react';
import { ParticipantPresence } from '../types/socket';
import styles from './PresencePanel.module.css';

interface PresencePanelProps {
  presence: ParticipantPresence[];
  isConnected: boolean;
}

/**
 * PRESENCE PANEL component
 * Collapsible bottom panel that shows all participants in workspace
 * Displays user/viewer names with role badges
 *
 * @param presence - List of connected participants
 * @param isConnected - WebSocket connection status
 */
export function PresencePanel({ presence, isConnected }: PresencePanelProps): React.JSX.Element {
  const [isExpanded, setIsExpanded] = useState(true);

  // TOGGLE panel expanded/collapsed state
  const togglePanel = () => {
    setIsExpanded(!isExpanded);
  };

  // COUNT users and viewers
  const userCount = presence.filter((p) => p.role === 'user').length;
  const viewerCount = presence.filter((p) => p.role === 'viewer').length;

  return (
    <div className={`${styles.presencePanel} ${isExpanded ? styles.expanded : styles.collapsed}`}>
      {/* PANEL HEADER - Always visible */}
      <div className={styles.header} onClick={togglePanel}>
        <div className={styles.headerLeft}>
          <span className={styles.icon}>{isExpanded ? '▼' : '▲'}</span>
          <span className={styles.title}>
            Teilnehmer:innen ({presence.length})
          </span>
          {!isExpanded && (
            <span className={styles.summary}>
              {userCount > 0 && (userCount === 1 ? userCount : `${userCount} Ersteller:innen`)}
              {userCount > 0 && viewerCount > 0 && ', '}
              {viewerCount > 0 && (viewerCount === 1 ? viewerCount : `${viewerCount} Betrachter:innen`)}
            </span>
          )}
        </div>
        <div className={styles.headerRight}>
          <span className={`${styles.connectionStatus} ${isConnected ? styles.connected : styles.disconnected}`}>
            {isConnected ? '● Verbunden' : '○ Getrennt'}
          </span>
        </div>
      </div>

      {/* PANEL CONTENT - Only visible when expanded */}
      {isExpanded && (
        <div className={styles.content}>
          {presence.length === 0 ? (
            <div className={styles.emptyState}>
              <p>Noch keine Teilnehmer:innen</p>
              <p className={styles.emptyHint}>Teilnehmer:innen erscheinen hier, sobald sie beitreten</p>
            </div>
          ) : (
            <div className={styles.participantList}>
              {presence.map((participant) => (
                <div key={participant.socketId} className={styles.participant}>
                  <span className={styles.participantName}>{participant.name}</span>
                  <span className={`${styles.roleBadge} ${styles[participant.role]}`}>
                    {participant.role === 'user' ? 'Ersteller:in' : 'Betrachter:in'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
